import { Plane, Building2, Package, Users, CheckCircle, ArrowRight, GraduationCap, Heart, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

export const ServicesSection = () => {
  const { t } = useLanguage();

  const services = [
    {
      icon: Plane,
      title: t.services.airport.title,
      description: t.services.airport.description,
      features: t.services.airport.features,
      highlight: t.services.airport.highlight,
    },
    {
      icon: Building2,
      title: t.services.hotel.title,
      description: t.services.hotel.description,
      features: t.services.hotel.features,
      highlight: t.services.hotel.highlight,
    },
    {
      icon: Package,
      title: t.services.courier.title,
      description: t.services.courier.description,
      features: t.services.courier.features,
      highlight: t.services.courier.highlight,
    },
    {
      icon: Users,
      title: t.services.personal.title,
      description: t.services.personal.description,
      features: t.services.personal.features,
      highlight: t.services.personal.highlight,
    },
    {
      icon: GraduationCap,
      title: t.services.school.title,
      description: t.services.school.description,
      features: t.services.school.features,
      highlight: t.services.school.highlight,
    },
    {
      icon: Heart,
      title: t.services.elderly.title,
      description: t.services.elderly.description,
      features: t.services.elderly.features,
      highlight: t.services.elderly.highlight,
    },
    {
      icon: Briefcase,
      title: t.services.business.title,
      description: t.services.business.description,
      features: t.services.business.features,
      highlight: t.services.business.highlight,
    },
  ];

  return (
    <section id="tjanster" className="section-spacing bg-[#111111]" data-testid="services-section">
      <div className="section-container">
        {/* Header */}
        <div className="text-left mb-16">
          <span className="text-yellow-500 text-sm font-semibold tracking-widest uppercase mb-4 block">
            {t.services.title}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t.services.subtitle}
          </h2>
          <p className="text-neutral-400 text-lg max-w-3xl">
            {t.services.description}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div
              key={service.title}
              className="bg-neutral-900/50 border border-white/5 rounded-2xl p-8 hover:border-yellow-500/20 transition-colors"
              data-testid={`service-card-${index}`}
            >
              {/* Header */}
              <div className="flex items-start gap-5 mb-6">
                <div className="service-icon flex-shrink-0">
                  <service.icon className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {service.title}
                  </h3>
                  <p className="text-neutral-300 text-base leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>

              {/* Features List */}
              <div className="space-y-3 mb-6 pl-2">
                {service.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-400">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Highlight */}
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
                <p className="text-yellow-500 font-medium text-sm">
                  {service.highlight}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="glass-card rounded-2xl p-8 lg:p-12 max-w-3xl mx-auto">
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
              {t.services.specialService}
            </h3>
            <p className="text-neutral-400 mb-6">
              {t.services.specialServiceDesc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:0480474717">
                <Button className="btn-primary">
                  {t.services.callUsBtn}: 0480 47 47 17
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="#kontakt">
                <Button className="btn-secondary">
                  {t.services.sendRequest}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
